import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { errMsg } from '../api';
import { formatUGX } from '../mock';
import { useCart } from '../context/CartContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import { Search, Plus, Minus, Trash2, ShoppingCart, Pill, ArrowRight, RefreshCw, Loader2, AlertTriangle, Clock } from 'lucide-react';

const POSPage = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { items, addItem, changeQty, removeItem, customer, setCustomer, subtotal, totalItems } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('All');
  const [selected, setSelected] = useState(null);
  const [selectedQty, setSelectedQty] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/products');
      setProducts(res.data);
    } catch (e) {
      toast({ title: 'Could not load products', description: errMsg(e), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const categories = useMemo(() => Array.from(new Set(products.map((p) => p.category))).sort(), [products]);

  const filtered = useMemo(() => products.filter((p) => {
    const q = query.toLowerCase();
    const match = p.name.toLowerCase().includes(q) || (p.generic || '').toLowerCase().includes(q) || (p.barcode || '').includes(q);
    return match && (category === 'All' || p.category === category);
  }), [products, query, category]);

  const openProduct = (p) => { if (p.available > 0) { setSelected(p); setSelectedQty(1); } };
  const confirmAdd = () => {
    if (!selected) return;
    addItem(selected, selectedQty);
    toast({ title: 'Added to cart', description: `${selectedQty} × ${selected.name}` });
    setSelected(null);
  };

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-extrabold text-4xl text-slate-900">Point of Sale</h1>
          <p className="text-slate-500 mt-2 text-[15px]">Serve walk-in customers — add items, then proceed to payment.</p>
        </div>
        <Button variant="outline" onClick={load} className="gap-2 rounded-lg"><RefreshCw size={16} /> Refresh stock</Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-6 items-start">
        <div className="lg:col-span-2">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search drug, generic or barcode..." className="pl-11 h-12 rounded-xl border-slate-200 bg-white" />
            </div>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="h-12 w-full sm:w-52 rounded-xl bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All categories</SelectItem>
                {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-24 text-slate-400"><Loader2 className="animate-spin mr-2" /> Loading products…</div>
          ) : (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4 mt-4">
              {filtered.map((p) => {
                const out = p.available <= 0;
                return (
                  <button key={p.id} disabled={out} onClick={() => openProduct(p)}
                    className="text-left bg-white rounded-2xl border border-slate-200 p-4 hover:border-blue-300 hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                    <div className="flex items-start justify-between">
                      <div className="h-10 w-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center"><Pill size={18} /></div>
                      <Badge variant="outline" className={`${out ? 'border-slate-200 text-slate-400' : p.low_stock ? 'border-amber-200 bg-amber-50 text-amber-700' : 'border-slate-200 text-slate-500'}`}>
                        {out ? 'Out of stock' : `Stock ${p.available}`}
                      </Badge>
                    </div>
                    <div className="font-display font-bold text-slate-900 mt-3 leading-tight">{p.name}</div>
                    <div className="text-slate-400 text-xs mt-0.5">{p.generic} · {p.form}</div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="font-display font-bold text-blue-600">{formatUGX(p.price)}</div>
                      <div className="flex gap-1.5">
                        {p.low_stock && !out && <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700 gap-1 text-[11px]"><AlertTriangle size={11} /> Low</Badge>}
                        {p.near_expiry && <Badge variant="outline" className="border-red-200 bg-red-50 text-red-700 gap-1 text-[11px]"><Clock size={11} /> Expiring</Badge>}
                      </div>
                    </div>
                  </button>
                );
              })}
              {filtered.length === 0 && <div className="col-span-full text-center text-slate-400 py-16">No products match your search.</div>}
            </div>
          )}
        </div>

        {/* Cart */}
        <div className="bg-white rounded-2xl border border-slate-200 sticky top-4">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
            <ShoppingCart size={18} className="text-slate-600" />
            <h3 className="font-display font-bold text-slate-900">Current sale</h3>
            {totalItems > 0 && <Badge className="ml-auto bg-blue-100 text-blue-700">{totalItems} item(s)</Badge>}
          </div>
          <div className="max-h-[340px] overflow-y-auto divide-y divide-slate-100">
            {items.length === 0 ? (
              <div className="py-16 text-center text-slate-400 text-sm px-6">Tap a product to add it to the sale.</div>
            ) : items.map((i) => (
              <div key={i.id} className="flex items-center gap-3 px-5 py-3">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-slate-800 text-sm truncate">{i.name}</div>
                  <div className="text-slate-400 text-xs">{formatUGX(i.price)} each</div>
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => changeQty(i.id, -1)} className="h-7 w-7 rounded-lg border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-50"><Minus size={13} /></button>
                  <span className="w-6 text-center font-semibold text-slate-800 text-sm">{i.qty}</span>
                  <button onClick={() => changeQty(i.id, 1)} className="h-7 w-7 rounded-lg border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-50"><Plus size={13} /></button>
                </div>
                <div className="w-20 text-right font-semibold text-slate-800 text-sm">{formatUGX(i.price * i.qty)}</div>
                <button onClick={() => removeItem(i.id)} className="text-slate-300 hover:text-red-500"><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
          <div className="p-5 border-t border-slate-100 space-y-4">
            <div>
              <Label className="text-slate-600 text-sm">Customer (optional)</Label>
              <Input value={customer} onChange={(e) => setCustomer(e.target.value)} placeholder="Walk-in" className="mt-1.5 h-10 rounded-lg" />
            </div>
            <div className="flex items-center justify-between pt-1">
              <span className="text-slate-500">Total</span>
              <span className="font-display font-extrabold text-2xl text-slate-900">{formatUGX(subtotal)}</span>
            </div>
            <Button onClick={() => navigate('/app/checkout')} disabled={items.length === 0}
              className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold text-[16px] gap-2">
              Proceed to payment <ArrowRight size={18} />
            </Button>
          </div>
        </div>
      </div>

      {/* Product detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-display text-xl">Add to sale</DialogTitle></DialogHeader>
          {selected && (
            <div>
              <div className="flex items-start gap-3">
                <div className="h-12 w-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0"><Pill size={22} /></div>
                <div className="min-w-0">
                  <div className="font-display font-bold text-slate-900">{selected.name}</div>
                  <div className="text-slate-400 text-sm">{selected.generic} · {selected.form} · {selected.category}</div>
                  <div className="mt-1 flex items-center gap-2">
                    <span className="font-display font-bold text-blue-600">{formatUGX(selected.price)}</span>
                    <Badge variant="outline" className="border-slate-200 text-slate-500">Available: {selected.available}</Badge>
                  </div>
                </div>
              </div>
              <div className="mt-6">
                <Label className="text-slate-600 text-sm">Quantity</Label>
                <div className="mt-2 flex items-center gap-3">
                  <button onClick={() => setSelectedQty((q) => Math.max(1, q - 1))} className="h-11 w-11 rounded-xl border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-50"><Minus size={18} /></button>
                  <Input type="number" value={selectedQty}
                    onChange={(e) => setSelectedQty(Math.max(1, Math.min(Number(e.target.value) || 1, selected.available)))}
                    className="h-11 w-20 text-center rounded-xl font-semibold text-lg" />
                  <button onClick={() => setSelectedQty((q) => Math.min(selected.available, q + 1))} className="h-11 w-11 rounded-xl border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-50"><Plus size={18} /></button>
                  <span className="ml-auto font-display font-bold text-slate-900 text-lg">{formatUGX(selected.price * selectedQty)}</span>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setSelected(null)}>Cancel</Button>
            <Button onClick={confirmAdd} className="bg-blue-600 hover:bg-blue-700 text-white gap-2"><ShoppingCart size={16} /> Add to cart</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default POSPage;
